/**
 * Market Cart Validator
 * Handles cart validation when markets switch and removes unavailable items
 */

class MarketCartValidator {
  constructor() { 
    this.currentMarket = this.getCurrentMarket();
    this.removedItemsKey = 'market-removed-items';
    this.init();
  }

  init() {
    // Check if market changed since last page load
    this.checkForMarketChangeOnLoad();
    
    this.setupMarketSwitchListeners();
    this.showRemovedItemsMessage();
    this.setupCartClearListeners();
    this.setupPreMarketSwitchStorage();
    
    // Add to window for manual testing
    window.marketCartValidator = this;
  }

  checkForMarketChangeOnLoad() {
    const lastMarketKey = 'last-market';
    const lastMarket = localStorage.getItem(lastMarketKey);
    
    if (lastMarket && lastMarket !== this.currentMarket) { 
      // NOTE: Cart count should have been stored BEFORE market switch by handleMarketSwitch
      // If not stored, we can't detect natural Shopify removals
      const storedCount = localStorage.getItem('cart-count-before-validation');
      if (!storedCount) {
      }
      
      // Small delay to ensure page is fully loaded
      setTimeout(() => {
        this.validateCartForMarket(this.currentMarket);
      }, 1000);
    }
    
    // Update stored market
    localStorage.setItem(lastMarketKey, this.currentMarket);
  }

  getCurrentMarket() {
    // Try multiple sources to determine current market
    let market = null;
    const sources = [];
    
    // Method 1: Check Shopify object
    if (window.Shopify?.country) {
      market = window.Shopify.country;
      sources.push(`Shopify.country: ${market}`);
    }
    
    // Method 2: Check localization data
    if (!market && window.localization?.country?.iso_code) {
      market = window.localization.country.iso_code;
      sources.push(`localization.country.iso_code: ${market}`);
    }
    
    // Method 3: Check document data attributes
    if (!market) {
      const dataMarket = document.documentElement.getAttribute('data-market') || 
                        document.documentElement.getAttribute('data-country');
      if (dataMarket) {
        market = dataMarket;
        sources.push(`document data attribute: ${market}`);
      }
    }
    
    // Method 4: Check URL pattern (e.g. /de-de/, /fr-fr/)
    if (!market) {
      const urlMatch = window.location.pathname.match(/^\/([a-z]{2})-([a-z]{2})\//);
      if (urlMatch) {
        market = urlMatch[2].toUpperCase();
        sources.push(`URL pattern: ${market}`);
      }
    }
    
    // Method 5: Check meta tags
    if (!market) {
      const metaCountry = document.querySelector('meta[name="country"]') || 
                         document.querySelector('meta[property="og:locale"]');
      if (metaCountry) {
        const content = metaCountry.content;
        if (content.includes('_')) {
          market = content.split('_')[1];
          sources.push(`meta tag: ${market}`);
        }
      }
    }
    
    // Fallback to default
    if (!market) {
      market = 'GB'; // Default fallback
      sources.push('fallback: GB');
    }
    
    return market.toUpperCase();
  }

  setupMarketSwitchListeners() {
    // Debug: Find and log all forms on the page
    const allForms = document.querySelectorAll('form');
    allForms.forEach((form, index) => {

    });
    
    // Listen for localization form submissions
    document.addEventListener('submit', (event) => {
        const formData = new FormData(event.target);
      
      if (event.target.matches('form[action*="localization"]')) {
        this.handleMarketSwitch(event);
      } else {
      }
    });

    // Listen for manual market switches via custom events
    document.addEventListener('market:changed', (event) => {
      this.validateCartForMarket(event.detail.newMarket);
    });
    
    // Also listen for any changes to select elements that might be country selectors
    document.addEventListener('change', (event) => {
      if (event.target.name === 'country_code' || event.target.name === 'localization[country_code]') {
        // Trigger validation immediately on country change
        const newMarket = event.target.value;
        if (newMarket && newMarket !== this.currentMarket) {
          this.validateCartForMarket(newMarket);
        }
      }
    });
    
    // Listen specifically to the localization forms
    const headerForm = document.getElementById('header-localization-form');
    const footerForm = document.getElementById('footer-localization');
    
    if (headerForm) {
      // Add multiple event types to catch any submission method
      ['submit', 'click'].forEach(eventType => {
        headerForm.addEventListener(eventType, (event) => {
          if (eventType === 'submit') {
            this.handleMarketSwitch(event);
          }
        });
      });
      
      // Also listen to any clicks within the form
      headerForm.addEventListener('click', (event) => {
        if (event.target.tagName === 'BUTTON' || event.target.type === 'submit') {
          setTimeout(() => this.handleMarketSwitch({target: headerForm}), 100);
        }
      });
    }
    
    if (footerForm) {
      // Add multiple event types to catch any submission method  
      ['submit', 'click'].forEach(eventType => {
        footerForm.addEventListener(eventType, (event) => {
          if (eventType === 'submit') {
            this.handleMarketSwitch(event);
          }
        });
      });
      
      // Also listen to any clicks within the form
      footerForm.addEventListener('click', (event) => {
        if (event.target.tagName === 'BUTTON' || event.target.type === 'submit') {
          setTimeout(() => this.handleMarketSwitch({target: footerForm}), 100);
        }
      });
    }
    
    // Listen for URL changes that might indicate market switches
    let currentUrl = window.location.href;
    const checkForMarketChange = () => {
      if (window.location.href !== currentUrl) {
        const newMarket = this.getCurrentMarket();
        if (newMarket !== this.currentMarket) { 
          this.currentMarket = newMarket;
          this.validateCartForMarket(newMarket);
        }
        currentUrl = window.location.href;
      }
    };
    
    // Check periodically for URL/market changes
    setInterval(checkForMarketChange, 1000);
    
    // Also check on popstate (back/forward navigation)
    window.addEventListener('popstate', checkForMarketChange);
  }

  setupCartClearListeners() {

    // Listen for cart updates that might indicate clearing
    document.addEventListener('cart:updated', (event) => {
      if (event.detail && event.detail.cart) {
        const cart = event.detail.cart;
        
        if (cart.item_count === 0) {
          this.clearRemovedItemsMessage();
        }
      }
    });
    
    // Listen for specific cart clear events
    document.addEventListener('cart:clear', () => {
      this.clearRemovedItemsMessage();
    });
    
    // Monitor cart drawer for empty state
    const checkCartEmpty = () => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const emptyIndicator = cartDrawer.querySelector('[data-empty="true"]') || 
                              cartDrawer.querySelector('.cart-empty');
        if (emptyIndicator) {
          this.clearRemovedItemsMessage();
        }
      }
    };
    
    // Check periodically if cart becomes empty
    setInterval(checkCartEmpty, 2000);
  }

  setupPreMarketSwitchStorage() {
    // Store cart count whenever we detect potential market switch triggers
    
    // Method 1: Listen for any form submissions that might change markets
    document.addEventListener('submit', async (event) => {
      const form = event.target;
      
      // Check if this form might trigger a market change
      const hasMarketFields = form.querySelector('[name="country_code"], [name="market"], [name="locale"]');
      const isLocalizationForm = form.classList.contains('localization-form') || 
                                form.id.includes('localization') ||
                                form.action.includes('localization');
      
      if (hasMarketFields || isLocalizationForm) {
        const cart = await this.getCart();
        const cartCount = cart ? cart.item_count : 0;
        localStorage.setItem('cart-count-before-validation', cartCount.toString());
      }
    });
    
    // Method 2: Store cart count when page is about to unload (covers manual navigation)
    window.addEventListener('beforeunload', async () => {
      const cart = await this.getCart();
      const cartCount = cart ? cart.item_count : 0;
      localStorage.setItem('cart-count-before-navigation', cartCount.toString());
    });
    
    // Method 3: Store cart count when any market-related links are clicked
    document.addEventListener('click', async (event) => {
      const link = event.target.closest('a');
      if (!link) return;
      
      const href = link.href || '';
      const hasMarketInUrl = href.includes('/en-') || href.includes('/fr-') || 
                            href.includes('/de-') || href.includes('/ie-') ||
                            href.includes('market=') || href.includes('country=');
      
      if (hasMarketInUrl) {
        const cart = await this.getCart();
        const cartCount = cart ? cart.item_count : 0;
        localStorage.setItem('cart-count-before-validation', cartCount.toString());
      }
    });
  }

  async handleMarketSwitch(event) {   

  window.dataLayer = window.dataLayer || [];
  dataLayer.push({
    event: 'market_card_validator_handleMarketSwitch'
  });    
    
    const formData = new FormData(event.target);
    const newMarket = formData.get('country_code');
    
    if (newMarket && newMarket !== this.currentMarket) { 
      // Store current cart count before validation to detect natural Shopify removals
      const currentCart = await this.getCart();
      const cartCountBeforeValidation = currentCart ? currentCart.item_count : 0;
      localStorage.setItem('cart-count-before-validation', cartCountBeforeValidation.toString());
      
      // Validate cart before the form submission completes
      await this.validateCartForMarket(newMarket);
    }
  }

  async validateCartForMarket(newMarket) {
    
  window.dataLayer = window.dataLayer || [];
  dataLayer.push({
    event: 'market_card_validator_ValidateCartForMarket'
  });    
    
    
    try {
      // Check if cart quantity decreased due to market change
      const expectedCartCountStr = localStorage.getItem('cart-count-before-validation') || 
                                   localStorage.getItem('cart-count-before-navigation');
      const expectedCartCount = parseInt(expectedCartCountStr || '0');
      
      // STEP 1: Get current cart (after any natural Shopify changes)
      const currentCart = await this.getCart();
      const actualCartCount = currentCart ? currentCart.item_count : 0;
      
      // Check if Shopify naturally removed items due to market change
      const shopifyRemovedItems = expectedCartCount > 0 && expectedCartCount > actualCartCount;
      if (shopifyRemovedItems) {
        const removedCount = expectedCartCount - actualCartCount;
      }
      
      if (!currentCart || currentCart.item_count === 0) {
        if (shopifyRemovedItems) {
          this.setRemovedItemsMessage([{ 
            product_title: `${expectedCartCount} item${expectedCartCount !== 1 ? 's' : ''}`,
            variant_title: 'removed due to availability', 
            quantity: expectedCartCount 
          }]);
          await this.openCartDrawerRobustly();
          setTimeout(() => this.checkAndShowRemovedItemsMessage(), 1000);
        }
        return;
      }

      // STEP 2: Check for items completely unavailable in the new market
      console.log(`🔍 Checking availability of ${currentCart.items.length} items for market ${newMarket}`);
      const unavailableItems = await this.checkItemsAvailability(currentCart.items, newMarket);
      
      if (unavailableItems.length > 0) {
        console.log(`❌ Found ${unavailableItems.length} items unavailable in market ${newMarket}:`, 
          unavailableItems.map(item => `${item.product_title} (${item.variant_title})`));
      } else {
        console.log(`✅ All items available in market ${newMarket}`);
      }
      
      let shouldOpenCart = shopifyRemovedItems; // Open cart if Shopify removed items
      let allRemovedItems = [];
      
      // Add Shopify-removed items to the message if any
      if (shopifyRemovedItems) {
        const removedCount = expectedCartCount - actualCartCount;
        allRemovedItems.push({
          product_title: `${removedCount} item${removedCount !== 1 ? 's' : ''}`,
          variant_title: 'removed due to availability in this market',
          quantity: removedCount
        });
      }
      
      if (!unavailableItems || unavailableItems.length === 0) {
        if (shouldOpenCart) {
          this.setRemovedItemsMessage(allRemovedItems);
          await this.openCartDrawerRobustly();
          setTimeout(() => {
            this.checkAndShowRemovedItemsMessage();
          }, 1000);
        }
        return;
      }
      
      // STEP 3: Remove the unavailable items
      const manuallyRemovedItems = await this.removeUnavailableItems(unavailableItems);
      
      if (manuallyRemovedItems && manuallyRemovedItems.length > 0) {
        allRemovedItems = allRemovedItems.concat(manuallyRemovedItems);
        shouldOpenCart = true;
      }
      
      // STEP 4: Open cart and show message if any items were removed
      if (shouldOpenCart && allRemovedItems.length > 0) {
        this.setRemovedItemsMessage(allRemovedItems);
        
        // Open cart drawer using multiple methods for maximum reliability
        
        // Method 1: Use the robust method
        await this.openCartDrawerRobustly();
        
        // Method 2: Ensure event is dispatched (most reliable)
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { 
          bubbles: true,
          detail: { 
            reason: 'market-cart-validation',
            removedItems: allRemovedItems.length
          } 
        }));
        
        // Method 3: Direct cart icon click as last resort
        setTimeout(() => {
          const cartIcon = document.getElementById('cart-icon');
          if (cartIcon) {
            cartIcon.click();
          }
        }, 500);
        
        // Show message after cart is opened
        setTimeout(() => {
          this.checkAndShowRemovedItemsMessage();
        }, 1000);
        
        const totalRemoved = allRemovedItems.length;
      } 
      
      // Clean up stored cart counts
      localStorage.removeItem('cart-count-before-validation');
      localStorage.removeItem('cart-count-before-navigation');

  window.dataLayer = window.dataLayer || [];
  dataLayer.push({
    event: 'market_card_validator_Success'
  });    
          
    } catch (error) {
      console.error('❌ Error in simple market validation:', error);
      localStorage.removeItem('cart-count-before-validation');
      localStorage.removeItem('cart-count-before-navigation');

  window.dataLayer = window.dataLayer || [];
  dataLayer.push({
    event: 'market_card_validator_Error'
  });    
    
      
    }
  }

  openCartDrawer() {
    // Method 1: Standard event
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
    
    // Method 2: Try finding and clicking cart icon
    const cartIcon = document.getElementById('cart-icon');
    if (cartIcon) {
      cartIcon.click();
    }
    
    // Method 3: Try finding cart drawer directly
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
    
    // Method 4: Try other common cart triggers
    const cartTrigger = document.querySelector('[data-cart-drawer-toggle]') || 
                       document.querySelector('.cart-drawer-toggle') ||
                       document.querySelector('[data-open-cart]');
    if (cartTrigger) {
      cartTrigger.click();
    }
  }

  async openCartDrawerRobustly() {
    let attempts = 0;
    const maxAttempts = 3;
    const retryDelay = 1000;
    
    while (attempts < maxAttempts) {
      attempts++;
      
      // Method 1: Standard events
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
      
      // Method 2: Try cart icon with multiple selectors
      const cartSelectors = [
        '#cart-icon',
        '.cart-icon',
        '[data-cart-icon]',
        '.header__icon--cart',
        'a[href="/cart"]',
        '.cart-link',
        '.cart-drawer-toggle'
      ];
      
      for (const selector of cartSelectors) {
        const element = document.querySelector(selector);
        if (element) {
          element.click();
          break;
        }
      }
      
      // Method 3: Try cart drawer directly
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        
        // Try different open methods
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
        
        if (typeof cartDrawer.show === 'function') {  
          cartDrawer.show();
        }
        
        // Try setting attributes
        cartDrawer.setAttribute('aria-hidden', 'false');
        cartDrawer.setAttribute('open', '');
        cartDrawer.classList.add('open', 'active');
        
        // Try focusing
        if (cartDrawer.focus) {
          cartDrawer.focus();
        }
      }
      
      // Method 4: Try programmatic cart state change      
      // Trigger side drawer open if exists
      const sideDrawer = document.querySelector('side-drawer');
      if (sideDrawer && typeof sideDrawer.openDrawer === 'function') {
        sideDrawer.openDrawer();
      }
      
      // Wait and check if cart opened
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const isCartOpen = this.isCartDrawerOpen();
      
      if (isCartOpen) {
        return true;
      }
      
      if (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
    
    // Fallback: Show message without relying on cart being open
    this.showRemovedItemsMessageDirectly();
    
    return false;
  }

  isCartDrawerOpen() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return false;
    
    // Check multiple indicators of open state
    const isAriaHidden = cartDrawer.getAttribute('aria-hidden') === 'false';
    const hasOpenAttr = cartDrawer.hasAttribute('open');
    const hasOpenClass = cartDrawer.classList.contains('open') || cartDrawer.classList.contains('active');
    const isVisible = cartDrawer.offsetWidth > 0 && cartDrawer.offsetHeight > 0;
    
    return isAriaHidden || hasOpenAttr || hasOpenClass || isVisible;
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  async checkItemsAvailability(items, market) {
    const unavailableItems = [];

    for (const item of items) {

      try {
        // Check if the variant is available in the new market
        const isAvailable = await this.checkVariantAvailability(item.variant_id, market);
        
        if (!isAvailable) {
          unavailableItems.push({
            id: item.id,
            variant_id: item.variant_id,
            product_title: item.product_title,
            variant_title: item.variant_title,
            quantity: item.quantity,
            line: item.index + 1
          });
        } else {
        }
      } catch (error) {
        console.error(`Error checking availability for variant ${item.variant_id}:`, error);
        // If we can't check availability, assume it's unavailable to be safe
        unavailableItems.push({
          id: item.id,
          variant_id: item.variant_id,
          product_title: item.product_title,
          variant_title: item.variant_title,
          quantity: item.quantity,
          line: item.index + 1
        });
      }
    }

    return unavailableItems;
  }

    async checkVariantAvailability(variantId, market) {
    try {
      console.log(`🔍 Checking variant ${variantId} availability for market ${market}`);
      
      // DEFINITIVE APPROACH: 
      // Check the variant API which returns market-aware availability status
      // The 'available' field in the response is the definitive indicator
      
      try {
        // Fetch variant data in the context of the target market
        // First, try to get it in the market context if possible
        let apiUrl = `/variants/${variantId}.js`;
        const marketPrefix = this.getMarketUrlPrefix(market);
        
        // If we can construct a market-specific URL, try that first
        if (marketPrefix && marketPrefix !== this.getMarketUrlPrefix(this.getCurrentMarket())) {
          try {
            const marketSpecificUrl = `${marketPrefix}/variants/${variantId}.js`;
            console.log(`🧪 Testing market-specific variant API: ${marketSpecificUrl}`);
            
            const marketResponse = await fetch(marketSpecificUrl, {
              cache: 'no-cache',
              headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              }
            });
            
            if (marketResponse.ok) {
              const variantData = await marketResponse.json();
              console.log(`📊 Variant ${variantId} data for market ${market}:`, {
                available: variantData.available,
                title: variantData.title,
                name: variantData.name
              });
              
              if (variantData.available === false) {
                console.log(`❌ Variant ${variantId} is NOT available in market ${market} (available: false)`);
                return false;
              } else {
                console.log(`✅ Variant ${variantId} is available in market ${market} (available: ${variantData.available})`);
                return true;
              }
            } else if (marketResponse.status === 404) {
              console.log(`❌ Variant ${variantId} not found in market ${market} (404)`);
              return false;
            } else {
              console.log(`⚠️  Market-specific API returned ${marketResponse.status}, falling back to default API`);
            }
          } catch (marketError) {
            console.log(`⚠️  Market-specific API failed:`, marketError, '- falling back to default API');
          }
        }
        
        // Fallback: Use the default API (current market context)
        console.log(`🔄 Checking variant ${variantId} with current market context`);
        const apiResponse = await fetch(apiUrl, {
          cache: 'no-cache',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (apiResponse.status === 404) {
          console.log(`❌ Variant ${variantId} does not exist (404)`);
          return false;
        } else if (!apiResponse.ok) {
          console.log(`⚠️  Variant API returned ${apiResponse.status}, assuming available`);
          return true;
        }
        
        const variantData = await apiResponse.json();
        console.log(`📊 Variant ${variantId} data:`, {
          available: variantData.available,
          title: variantData.title,
          name: variantData.name
        });
        
        // Check the 'available' field - this is the definitive answer
        if (variantData.available === false) {
          console.log(`❌ Variant ${variantId} is NOT available (available: false)`);
          return false;
        } else {
          console.log(`✅ Variant ${variantId} is available (available: ${variantData.available})`);
          return true;
        }
        
      } catch (apiError) {
        console.log(`⚠️  API check failed for variant ${variantId}:`, apiError, '- assuming available');
        return true;
      }
      
    } catch (error) {
      console.error(`❌ Error checking variant ${variantId}:`, error);
      // Always default to available to prevent false removals
      return true;
    }
  }
  
  getMarketUrlPrefix(market) {
    // Map markets to their URL prefixes based on your site structure
    const marketPrefixes = {
      'GB': '/en-gb',
      'IE': '/en-ie', 
      'FR': '/fr-fr',
      'DE': '/de-de',
      'ES': '/es-es',
      'IT': '/it-it',
      'NL': '/nl-nl',
      'AT': '/de-at',
      'PL': '/pl-pl',
      'PT': '/pt-pt'
    };
    
    return marketPrefixes[market.toUpperCase()] || null;
  }

  async removeUnavailableItems(unavailableItems) {
    try {
      
      // Get current cart before removal to compare after
      const cartBefore = await this.getCart();
      
      if (!cartBefore || !cartBefore.items || cartBefore.items.length === 0) {
        return;
      }
      
      // Create a set of variant IDs in the current cart for validation
      const currentVariantIds = new Set();
      cartBefore.items.forEach((item) => {
        currentVariantIds.add(item.variant_id);
      });
      
      // Build updates object using variant IDs as keys
      const updates = {};
      const variantsToRemove = [];
      
      unavailableItems.forEach((item) => {
        if (currentVariantIds.has(item.variant_id)) {
          updates[item.variant_id] = 0; // Set quantity to 0 for this variant
          variantsToRemove.push(item.variant_id);
        }
      });

      if (Object.keys(updates).length === 0) {
        return;
      }


      // NEW APPROACH: Try simulating manual removal first (like clicking remove buttons)
      const manualSuccess = await this.simulateManualRemove(variantsToRemove);
      
      if (manualSuccess) {
        
        // Get updated cart for comparison
        const updatedCart = await this.getCart();
        const actuallyRemoved = this.compareCartChanges(cartBefore, updatedCart, unavailableItems);
        
        // Dispatch events
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: updatedCart }
        }));

        document.dispatchEvent(new CustomEvent('market:items-removed', {
          detail: { 
            removedItems: actuallyRemoved,
            cart: updatedCart 
          }
        }));

        // Return the actually removed items for verification
        return actuallyRemoved;
      }


      // FALLBACK: Use the original bulk update method
      // Use theme routes if available, otherwise fallback to hardcoded paths
      const updateUrl = '/cart/update.js'; // Use the correct Shopify endpoint

      // For bulk updates, use simpler format - sections not needed for /cart/update.js
      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          updates: updates
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Cart update failed:', response.status, errorText);
        console.error('Request was:', {
          url: updateUrl,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: {
            updates: updates
          }
        });
        
        // Try alternative approach using /cart/change.js with individual variant removals
        const manualSuccess = await this.simulateManualRemove(variantsToRemove);
        
        if (manualSuccess) {
          // Get updated cart and compare
          const updatedCart = await this.getCart();
          return this.compareCartChanges(cartBefore, updatedCart, unavailableItems);
        } else {
          return [];
        }
      }

      const responseData = await response.json();
      
      // Extract cart data (response should be the cart object directly for /cart/update.js)
      const updatedCart = responseData;
      
      // Refresh cart drawer sections since we didn't include them in the request
      setTimeout(async () => {
        
        // IMMEDIATE FIX: Remove stale items from DOM right away
        this.removeStaleItemsFromDOM(variantsToRemove);
        
        // Wait for cart API to reflect the changes before refreshing
        const expectedItemCount = cartBefore.item_count - variantsToRemove.length;
        
        const cartSynced = await this.waitForCartSync(expectedItemCount, variantsToRemove);
        
        if (cartSynced) {
          // Try multiple refresh strategies for maximum reliability
          await this.forceCartRefresh();
        } else {
          await this.forceCartRefresh();
        }
        
        // Also trigger cart drawer refresh event for other components
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
          bubbles: true,
          detail: { cart: updatedCart }
        }));
        
        // Update cart icon separately since it might not be included in cart refresh
        const cartIcon = document.getElementById('cart-icon-bubble');
        if (cartIcon) {
          fetch('/?section_id=cart-icon-bubble')
            .then(response => response.text())
            .then(html => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const newCartIcon = doc.getElementById('cart-icon-bubble');
              if (newCartIcon) {
                cartIcon.innerHTML = newCartIcon.innerHTML;
              }
            })
            .catch(error => console.log('Could not refresh cart icon:', error));
        }
      }, 100); // Reduced initial delay since we'll wait for sync
      
      // Compare what was actually removed vs what we expected
      const actuallyRemoved = this.compareCartChanges(cartBefore, updatedCart, unavailableItems);
      
      // Dispatch cart update events
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: updatedCart }
      }));

      document.dispatchEvent(new CustomEvent('market:items-removed', {
        detail: { 
          removedItems: actuallyRemoved,
          cart: updatedCart 
        }
      }));


      // Store the actually removed items for the message
      if (actuallyRemoved.length > 0) {
        this.setRemovedItemsMessage(actuallyRemoved);
        
        // Try multiple methods to open cart drawer
        setTimeout(() => {    
          
          // Method 1: Standard event
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
          
          // Method 2: Try finding and clicking cart icon
          const cartIcon = document.getElementById('cart-icon');
          if (cartIcon) {
            cartIcon.click();
          }
          
          // Method 3: Try finding cart drawer directly
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
          
        }, 500);
      }

      return actuallyRemoved;

    } catch (error) {
      console.error('Error removing unavailable items:', error);
      console.error('Stack trace:', error.stack);
      return [];
    }
  }

  async removeItemsByVariantId(variantsToRemove, cartBefore) {
    
    try {
      // Use theme routes if available, otherwise fallback to hardcoded paths
      const changeUrl = (typeof theme !== 'undefined' && theme.routes?.cartChange) || '/cart/change.js';
      
      // Try removing items one by one by variant ID
      const removedVariants = [];
      for (const variantId of variantsToRemove) {
        
        const requestBody = {
          id: variantId, // Use variant ID instead of line number
          quantity: 0,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname
        };
        
        
        const removeResponse = await fetch(changeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(requestBody)
        });
        
        
        if (!removeResponse.ok) {
          const errorText = await removeResponse.text();
          console.error(`Failed to remove variant ${variantId}:`, removeResponse.status, errorText);
          console.error('Failed request details:', {
            url: changeUrl,
            method: 'POST',
            body: requestBody,
            responseStatus: removeResponse.status,
            responseText: errorText
          });
        } else {
          const responseData = await removeResponse.json();
          removedVariants.push({
            variant_id: variantId,
            product_title: responseData.product_title,
            variant_title: responseData.variant_title,
            quantity: responseData.quantity
          });
        }
        
        // Small delay between requests to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Get updated cart
      const updatedCart = await this.getCart();
      const actuallyRemoved = this.compareCartChanges(cartBefore, updatedCart, []);
      
      
      // Dispatch events
      if (actuallyRemoved.length > 0) {
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: updatedCart }
        }));

        document.dispatchEvent(new CustomEvent('market:items-removed', {
          detail: { 
            removedItems: actuallyRemoved,
            cart: updatedCart 
          }
        }));
      }
      
      // Refresh cart drawer DOM properly after individual removals
      setTimeout(async () => {
        
        // IMMEDIATE FIX: Remove stale items from DOM right away
        this.removeStaleItemsFromDOM(variantsToRemove);
        
        // Wait for cart API to reflect the changes before refreshing
        const expectedItemCount = cartBefore.item_count - variantsToRemove.length;    
        
        const cartSynced = await this.waitForCartSync(expectedItemCount, variantsToRemove);
        
        if (cartSynced) {
          await this.forceCartRefresh();
        } else {
          await this.forceCartRefresh();
        }
        
        // Trigger refresh event for other components
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
          bubbles: true,
          detail: { cart: updatedCart }
        }));
      }, 100); // Reduced initial delay since we'll wait for sync
      
      return actuallyRemoved;
      
    } catch (error) {
      console.error('Error in variant-based removal method:', error);
      console.error('Stack trace:', error.stack);
      return [];
    }
  }

  compareCartChanges(cartBefore, cartAfter, expectedRemoved) {
    const actuallyRemoved = [];
    
    // Create maps for easier comparison
    const beforeMap = new Map();
    cartBefore.items.forEach(item => {
      beforeMap.set(item.variant_id, {
        quantity: item.quantity,
        product_title: item.product_title,
        variant_title: item.variant_title
      });
    });
    
    const afterMap = new Map();
    cartAfter.items.forEach(item => {
      afterMap.set(item.variant_id, {
        quantity: item.quantity,
        product_title: item.product_title,
        variant_title: item.variant_title
      });
    });
    
    // Check what was actually removed or had quantity reduced
    beforeMap.forEach((beforeItem, variantId) => {
      const afterItem = afterMap.get(variantId);
      
      if (!afterItem) {
        // Item completely removed
        actuallyRemoved.push({
          variant_id: variantId,
          product_title: beforeItem.product_title,
          variant_title: beforeItem.variant_title,
          quantity: beforeItem.quantity
        });
      } else if (afterItem.quantity < beforeItem.quantity) {
        // Quantity reduced
        const removedQuantity = beforeItem.quantity - afterItem.quantity;
        actuallyRemoved.push({
          variant_id: variantId,
          product_title: beforeItem.product_title,
          variant_title: beforeItem.variant_title,
          quantity: removedQuantity
        });
      }
    });
    
    return actuallyRemoved;
  }

  setRemovedItemsMessage(removedItems) {
    const messageData = {
      items: removedItems,
      timestamp: Date.now(),
      shown: false
    };
    
    try {
      localStorage.setItem(this.removedItemsKey, JSON.stringify(messageData));
    } catch (error) {
      console.error('Error storing removed items message:', error);
    }
  }

  getRemovedItemsMessage() {
    try {
      const data = localStorage.getItem(this.removedItemsKey);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error getting removed items message:', error);
      return null;
    }
  }

  clearRemovedItemsMessage() {
    try {
      localStorage.removeItem(this.removedItemsKey);
    } catch (error) {
      console.error('Error clearing removed items message:', error);
    }
  }

  showRemovedItemsMessage() {
    
    // Always set up the event listener for cart drawer opens
    document.addEventListener('on:cart-drawer:after-open', () => {
      this.checkAndShowRemovedItemsMessage();
    });


    // Also check immediately if cart drawer is already open
    const cartDrawer = document.querySelector('cart-drawer');
    
    if (cartDrawer && cartDrawer.getAttribute('aria-hidden') === 'false') {
      this.checkAndShowRemovedItemsMessage();
    }

    // Show message immediately if we're on the cart page
    if (window.location.pathname.includes('/cart')) {
      this.checkAndShowRemovedItemsMessage();
    }
  }

  checkAndShowRemovedItemsMessage() {
    const messageData = this.getRemovedItemsMessage();
    
    if (!messageData || messageData.shown || messageData.items.length === 0) {
      return;
    }

    // Display the notification
    this.displayRemovedItemsNotification(messageData.items);
    
    // Clear message immediately after showing
    this.clearRemovedItemsMessage();
  }

  displayRemovedItemsNotification(removedItems) {
    const count = removedItems.length;
    
    // Get translated message
    const message = count === 1 
      ? (theme.strings.marketRemovedItemsSingle || 'Removed 1 unavailable item')
      : (theme.strings.marketRemovedItemsMultiple || `Removed ${count} unavailable items`).replace('[count]', count);

    // Create and show notification
    this.showNotification(message, removedItems);
  }

  showNotification(message, removedItems) {
    // Create simple red text notification
    const notification = document.createElement('div');
    notification.className = 'market-removed-items-notification';
    notification.style.cssText = `
      color: red;
      font-size: 16px;
      margin: 16px 0;
      padding: 0;
      background: none;
      border: none;
      z-index: 1000;
    `;

    // Simple text content only
    notification.textContent = message;

    // Insert notification into cart
    this.insertNotificationIntoCart(notification);
  }

  showRemovedItemsDetails(removedItems) {
    const itemsList = removedItems.map(item => 
      `• ${item.product_title}${item.variant_title ? ` - ${item.variant_title}` : ''} (${item.quantity})`
    ).join('\n');

    const message = `${theme.strings.removedItemsDetails || 'The following items were removed because they are not available in your selected market:'}

${itemsList}`;

    alert(message);
  }

  insertNotificationIntoCart(notification) {
    
    // Try to insert into cart drawer first
    const cartDrawer = document.querySelector('cart-drawer .drawer__content');
    
    if (cartDrawer) {
      const cartItems = cartDrawer.querySelector('cart-items') || cartDrawer.querySelector('.cart-items');
      
      if (cartItems) {
        cartItems.parentNode.insertBefore(notification, cartItems);
        return;
      }
      
      // If no cart-items found, insert at the beginning of drawer content
      cartDrawer.insertBefore(notification, cartDrawer.firstChild);
      return;
    }

    // Fallback to cart page
    const cartItems = document.querySelector('cart-items') || document.querySelector('.cart-items');  
    
    if (cartItems) {
      cartItems.parentNode.insertBefore(notification, cartItems);
      return;
    }

    // Last resort - insert at top of main content
    const main = document.querySelector('main');
    
    if (main) {
      main.insertBefore(notification, main.firstChild);
    } else {
      console.error('Could not find anywhere to insert notification');
    }
  }

  showRemovedItemsMessageDirectly() {
    
    const messageData = this.getRemovedItemsMessage();
    if (!messageData || !messageData.items || messageData.items.length === 0) {
      return;
    }
    
    
    // Create a more prominent notification for when cart doesn't open
    const notification = document.createElement('div');
    notification.className = 'market-removed-items-popup';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      max-width: 400px;
      background: #fff;
      border: 2px solid #f59e0b;
      border-radius: 8px;
      padding: 16px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 9999;
      color: #333;
      font-family: system-ui, -apple-system, sans-serif;
    `;
    
    const count = messageData.items.length;
    const message = count === 1 
      ? (theme.strings?.marketRemovedItemsSingle || 'Removed 1 unavailable item')
      : (theme.strings?.marketRemovedItemsMultiple || `Removed ${count} unavailable items`).replace('[count]', count);
    
    notification.innerHTML = `
      <div style="display: flex; align-items: start; gap: 12px;">
        <div style="flex-shrink: 0; color: #f59e0b;">
          <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        </div>
        <div style="flex: 1;">
          <div style="font-weight: 600; margin-bottom: 8px;">${message}</div>
          <div style="font-size: 14px; color: #666; margin-bottom: 12px;">
            ${messageData.items.map(item => `• ${item.product_title}${item.variant_title ? ` - ${item.variant_title}` : ''}`).join('<br>')}
          </div>
          <div style="display: flex; gap: 8px;">
            <button onclick="document.querySelector('.market-removed-items-popup').remove(); window.location.href='/cart'" 
                    style="background: #f59e0b; color: white; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 14px;">
              View Cart
            </button>
            <button onclick="document.querySelector('.market-removed-items-popup').remove()" 
                    style="background: #e5e7eb; color: #374151; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 14px;">
              Dismiss
            </button>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Auto-dismiss after 10 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 10000);
    
    // Clear the message
    this.clearRemovedItemsMessage();
    
  }

  async compareCartAPIvsDOM() {
    
    // Get cart from API
    const apiCart = await this.getCart();
    if (apiCart && apiCart.items && apiCart.items.length > 0) {
      apiCart.items.forEach((item, index) => {
      });
    }
    
    // Get cart from DOM
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      return;
    }
    
    // Try different selectors
    const selectors = ['.cart-item', '[data-cart-item]', '.cart__item', '.line-item'];
    let domItems = [];
    
    for (const selector of selectors) {
      const items = cartDrawer.querySelectorAll(selector);
      if (items.length > 0) {
        domItems = Array.from(items);
        break;
      }
    }
    
    if (domItems.length === 0) {
    } else {
      domItems.forEach((item, index) => {
        const variantId = item.dataset.variantId || item.getAttribute('data-variant-id');
        const productName = item.querySelector('.cart-item__name, .cart__item-name, .line-item__name, [class*="name"]')?.textContent?.trim() || 'Unknown';
      });
    }
    
    // Compare
    const apiCount = apiCart?.item_count || 0;
    const domCount = domItems.length;
    
    if (apiCount === domCount) {
    } else {
      
      // Show which items are extra in DOM
      if (domCount > apiCount) {
        const apiVariantIds = new Set(apiCart?.items?.map(item => item.variant_id.toString()) || []);
        const extraDOMItems = domItems.filter(item => {
          const variantId = (item.dataset.variantId || item.getAttribute('data-variant-id'))?.toString();
          return variantId && !apiVariantIds.has(variantId);
        });

        extraDOMItems.forEach(item => {
          const variantId = item.dataset.variantId || item.getAttribute('data-variant-id');
          const productName = item.querySelector('.cart-item__name, .cart__item-name, .line-item__name, [class*="name"]')?.textContent?.trim() || 'Unknown';
        });
      }
    }
  }

  async forceCartRefresh() {
    
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      return;
    }

    const currentCartItems = cartDrawer.querySelectorAll('.cart-item');
    currentCartItems.forEach((item, index) => {
      const variantId = item.dataset.variantId;
      const productTitle = item.querySelector('.cart-item__name')?.textContent?.trim();
    });

    // Get actual cart state from API
    const actualCart = await this.getCart();
    if (actualCart?.items) {
      actualCart.items.forEach((item, index) => {
      });
    }

    try {
      // Method 1: Try cart-items refresh first
      const cartItems = document.querySelector('cart-items');
      
      if (cartItems && typeof cartItems.refresh === 'function') {
        await cartItems.refresh();
        
        // Check if refresh worked
        setTimeout(() => {
          const newCartItems = cartDrawer.querySelectorAll('.cart-item');
            if (newCartItems.length === (actualCart?.item_count || 0)) {
            return;
          }
        }, 100);
      }

      // Method 2: Manual section fetching like cart-drawer.js does
      
      // Get the sections to render from cart drawer
      const sectionsToRender = cartDrawer.getSectionsToRender ? 
        cartDrawer.getSectionsToRender() : 
        this.getDefaultCartSections(cartDrawer);
      
      const sectionIds = sectionsToRender.map(section => section.section);
      
      const response = await fetch(`?sections=${sectionIds.join(',')}`);
      if (!response.ok) {
        throw new Error(`Section fetch failed: ${response.status}`);
      }
      
      const sections = await response.json();
      
      // Debug the fetched sections
      Object.keys(sections).forEach(sectionKey => {
        const sectionHtml = sections[sectionKey];
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = sectionHtml;
        const cartItemsInSection = tempDiv.querySelectorAll('.cart-item');
      });
      
      // Use renderContents to update the cart drawer
      if (typeof cartDrawer.renderContents === 'function') {
        await cartDrawer.renderContents({ sections }, false); // Don't auto-open
        
        // Check if renderContents worked
        setTimeout(() => {
          const newCartItems = cartDrawer.querySelectorAll('.cart-item');
          if (newCartItems.length === (actualCart?.item_count || 0)) {
          }
        }, 100);
      } else {
        // Fallback: manually update each section
        sectionsToRender.forEach((section) => {
          const el = document.getElementById(section.id);
          if (el && sections[section.section]) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sections[section.section], 'text/html');
            const selectorEl = doc.querySelector(section.selector) || doc.body;
            el.innerHTML = selectorEl.innerHTML;
          }
        });
        
        // Check if manual update worked
        setTimeout(() => {
          const newCartItems = cartDrawer.querySelectorAll('.cart-item');   
        }, 100);
      }
      
    } catch (error) {
      
      // Try simulation approach before basic refresh
      await this.simulateNormalCartUpdate();
      
      // Last resort: basic cart drawer refresh
      if (typeof cartDrawer.refresh === 'function') {
        await cartDrawer.refresh(true);
        
        // Check if basic refresh worked
        setTimeout(() => {
          const newCartItems = cartDrawer.querySelectorAll('.cart-item');
        }, 100);
      }
    }
    
    // Final check after all refresh attempts
    setTimeout(() => {
      const finalCartItems = cartDrawer.querySelectorAll('.cart-item');
      const actualCart = this.getCart();
      actualCart.then(cart => {
        if (finalCartItems.length === (cart?.item_count || 0)) {
        }
      });
    }, 200);
  }

  getDefaultCartSections(cartDrawer) {
    // Default sections similar to cart-items.js
    const cartDrawerId = cartDrawer.closest('.shopify-section')?.id?.replace('shopify-section-', '') || 'cart-drawer';
    
    return [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-items',
        section: cartDrawerId,
        selector: 'cart-items'
      },
      {
        id: 'cart-drawer',
        section: cartDrawerId,
        selector: '.cart-drawer__summary'
      }
    ];
  }

  debugThemeRoutes() {
    if (typeof theme !== 'undefined') {
    }
  }

  async simulateNormalCartUpdate() {
    
    try {
      // Get current cart state
      const cart = await this.getCart();
      if (!cart) {
        return;
      }
      
      
      // Trigger the same events that happen during normal cart operations
      
      // 1. Dispatch cart:updated event like we do during removal
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cart }
      }));
      
      // 2. Dispatch on:line-item:change event like cart-items.js does
      document.dispatchEvent(new CustomEvent('on:line-item:change', {
        bubbles: true,
        detail: {
          cart: cart,
          variantId: null,
          oldQuantity: 0,
          newQuantity: 0
        }
      }));      
      
      // 3. Try to manually trigger cart-items update using the same logic as updateQuantity
      const cartItems = document.querySelector('cart-items');
      if (cartItems) {
        
        // Get sections like cart-items.js does
        const sectionsToRender = cartItems.getSectionsToRender ? cartItems.getSectionsToRender() : [];
        
        if (sectionsToRender.length > 0) {
          const sections = sectionsToRender.map(section => section.section);
          const response = await fetch(`?sections=${[...new Set(sections)]}`);
          
          if (response.ok) {
            const data = await response.json();
            
            // Update DOM like cart-items.js does
            sectionsToRender.forEach((section) => {
              const sectionEl = document.getElementById(section.id);
              if (!sectionEl) return;

              const { selector } = section;
              const el = sectionEl.querySelector(selector) || sectionEl;
              
              if (data[section.section]) {
                // Use the same helper method cart-items uses
                if (typeof cartItems.constructor.getElementHTML === 'function') {
                  el.innerHTML = cartItems.constructor.getElementHTML(
                    data[section.section],
                    selector
                  );
                } else {
                  // Fallback to manual parsing
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(data[section.section], 'text/html');
                  const selectorEl = doc.querySelector(selector) || doc.body;
                  el.innerHTML = selectorEl.innerHTML;
                }
              }
            });
            
            // Set dataset.empty like cart-items.js does
            cartItems.dataset.empty = cart.item_count === 0;
          }
        }
      }
      
      // 4. Force update cart icon using the same method as ProductForm
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        const iconResponse = await fetch('/?section_id=cart-icon-bubble');
        if (iconResponse.ok) {
          const iconHtml = await iconResponse.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(iconHtml, 'text/html');
          const newIcon = doc.getElementById('cart-icon-bubble');
          if (newIcon) {
            cartIconBubble.innerHTML = newIcon.innerHTML;
          }
        }
      }
      
    } catch (error) {
    }
  }

  async waitForCartSync(expectedItemCount, removedVariantIds) {
    const maxRetries = 10; // Maximum number of retries
    const retryInterval = 500; // Interval in milliseconds between retries

    for (let i = 0; i < maxRetries; i++) {
      const currentCart = await this.getCart();

      // Check both item count and that specific variants are gone
      const itemCountMatches = currentCart?.item_count === expectedItemCount;
      
      let variantsRemoved = true;
      if (removedVariantIds && removedVariantIds.length > 0) {
        const currentVariantIds = new Set(currentCart?.items?.map(item => item.variant_id) || []);
        variantsRemoved = removedVariantIds.every(variantId => !currentVariantIds.has(variantId));
        
      }

      if (itemCountMatches && variantsRemoved) {
        return true;
      }

      await new Promise(resolve => setTimeout(resolve, retryInterval));
    }
    
    const finalCart = await this.getCart();
    
    if (removedVariantIds && removedVariantIds.length > 0) {
      const stillInCart = removedVariantIds.filter(variantId => 
        finalCart?.items?.some(item => item.variant_id === variantId)
      );
    }
    
    return false;
  }

  removeStaleItemsFromDOM(removedVariantIds) {
    
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      return;
    }

    // Debug: Check different cart item selectors
    const selectors = ['.cart-item', '[data-cart-item]', '.cart__item', '.line-item'];
    let cartItems = null;
    
    for (const selector of selectors) {
      const items = cartDrawer.querySelectorAll(selector);
      if (items.length > 0 && !cartItems) {
        cartItems = items;
      }
    }
    
    if (!cartItems || cartItems.length === 0) {
      return;
    }

    // Debug: show all current items and their data
    Array.from(cartItems).forEach((item, index) => {
      const variantId = item.dataset.variantId || item.getAttribute('data-variant-id');
      const productName = item.querySelector('.cart-item__name, .cart__item-name, .line-item__name, [class*="name"]')?.textContent?.trim() || 'Unknown';
    });

    let removedCount = 0;
    
    removedVariantIds.forEach(variantId => {
      
      // Try multiple ways to find the item
      let itemToRemove = null;
      
      // Method 1: data-variant-id attribute
      itemToRemove = Array.from(cartItems).find(item => {
        const itemVariantId = item.dataset.variantId || item.getAttribute('data-variant-id');
        return itemVariantId && itemVariantId.toString() === variantId.toString();
      });
      
      if (!itemToRemove) {
        // Method 2: Look for variant ID in any data attribute
        itemToRemove = Array.from(cartItems).find(item => {
          return Object.values(item.dataset).some(value => value.toString() === variantId.toString());
        });
      }
      
      if (!itemToRemove) {
        // Method 3: Look for variant ID anywhere in the element
        itemToRemove = Array.from(cartItems).find(item => {
          return item.innerHTML.includes(variantId.toString());
        });
      }

      if (itemToRemove) {
        const productName = itemToRemove.querySelector('.cart-item__name, .cart__item-name, .line-item__name, [class*="name"]')?.textContent?.trim() || 'Unknown';      
        
        // Add visual feedback before removing
        itemToRemove.style.border = '2px solid red';
        itemToRemove.style.background = '#ffebee';
        itemToRemove.style.transition = 'all 0.3s ease-out';
        itemToRemove.style.opacity = '0.5';
        itemToRemove.style.transform = 'scale(0.95)';
        
        setTimeout(() => {
          itemToRemove.remove();
          
          // Verify removal
          const stillExists = cartDrawer.contains(itemToRemove);
        }, 300);
        
        removedCount++;
      } else {
        
      }
    });

    
    // Check final state
    setTimeout(() => {
      const remainingItems = cartDrawer.querySelectorAll('.cart-item, [data-cart-item], .cart__item, .line-item');
      
      if (remainingItems.length === 0) {
        // Update empty state
        const cartItemsContainer = document.querySelector('cart-items');
        if (cartItemsContainer) {
          cartItemsContainer.dataset.empty = 'true';
        }
        
        // Try to show empty message
        const emptyMessage = cartDrawer.querySelector('.cart-empty, [data-empty-state], .drawer__empty');
        if (emptyMessage) {
          emptyMessage.style.display = 'block';
          emptyMessage.style.visibility = 'visible';
        }
      }
    }, 500);
  }

  async simulateManualRemove(variantIds) {
    
    const cartItems = document.querySelector('cart-items');
    if (!cartItems) {
      return false;
    }


    // Get the sections to render like cart-items.js does
    const sectionsToRender = cartItems.getSectionsToRender ? cartItems.getSectionsToRender() : [];


    const sections = sectionsToRender.map(section => section.section);

    // Use cart/change.js like manual remove does
    const changeUrl = '/cart/change.js'; // Use the correct Shopify endpoint

    let success = false;

    for (const variantId of variantIds) {
      
      try {
        // Use FormData like the actual cart does
        const formData = new FormData();
        formData.append('id', variantId.toString());
        formData.append('quantity', '0');
        formData.append('sections', [...new Set(sections)].join(','));
        formData.append('sections_url', window.location.pathname);

        const response = await fetch(changeUrl, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        if (!response.ok) {
          const errorText = await response.text();
          continue;
        }

        const data = await response.json();

        // Update DOM using the same method as cart-items.js
        sectionsToRender.forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) {
            return;
          }

          const { selector } = section;
          const el = sectionEl.querySelector(selector) || sectionEl;
          
          if (data.sections && data.sections[section.section]) {
            
            // Use the same method as cart-items.js
            if (typeof cartItems.constructor.getElementHTML === 'function') {
              el.innerHTML = cartItems.constructor.getElementHTML(
                data.sections[section.section],
                selector
              );
            } else {
              // Fallback method
              const parser = new DOMParser();
              const doc = parser.parseFromString(data.sections[section.section], 'text/html');
              const selectorEl = doc.querySelector(selector) || doc.body;
              el.innerHTML = selectorEl.innerHTML;
            }
          } else {
          }
        });

        // Update cart-items dataset like manual remove does
        if (cartItems) {
          cartItems.dataset.empty = data.item_count === 0;  
        }

        // Dispatch the same event as manual remove
        cartItems.dispatchEvent(new CustomEvent('on:line-item:change', {
          bubbles: true,
          detail: {
            cart: data,
            variantId: variantId,
            oldQuantity: 1,
            newQuantity: 0
          }
        }));

        success = true;

      } catch (error) {
      }

      // Small delay between removals
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return success;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MarketCartValidator();
  });
} else {
  new MarketCartValidator();
}

// Export for manual initialization if needed
window.MarketCartValidator = MarketCartValidator; 